import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AuroraBackground } from "@/components/AuroraBackground";
import { AuroraShaderCanvas } from "@/components/AuroraShaderCanvas";
import { AuroraHeart } from "@/components/AuroraHeart";
import { TiltCard } from "@/components/TiltCard";
import { LocaleLink } from "@/components/LocaleLink";
import { StoreBadgeGroup, StartFreeButton } from "@/components/StoreBadges";
import { Reveal } from "@/components/Reveal";
import { AppVideo } from "@/components/AppVideo";
import { Proof } from "@/components/Proof";
import { HOME } from "@/constants/testIds";

const CLIP_BASE = "/assets/media/clips";
import {
  Heart,
  Sparkles,
  Shield,
  Compass,
  Lock,
  MessageCircle,
  Calendar,
  Brain,
  Users,
  Star,
  ChevronRight,
  CircleHelp,
} from "lucide-react";

const SHOWCASE = "/assets/store-showcase/apple-en";
const TILE_KEYS = [
  { tag: "Home", img: `${SHOWCASE}/aurora-home.webp`, bg: "tile-hero", k: "tileHome" },
  { tag: "AI Coach", img: `${SHOWCASE}/aurora-coach.webp`, bg: "tile-coach", k: "tileCoach" },
  { tag: "Conflicts", img: `${SHOWCASE}/aurora-conflicts.webp`, bg: "tile-emotional", k: "tileConflicts" },
  { tag: "Love Notes", img: `${SHOWCASE}/aurora-love-notes.webp`, bg: "tile-coach", k: "tileNotes" },
  { tag: "Journeys", img: `${SHOWCASE}/aurora-journeys.webp`, bg: "tile-journeys", k: "tileJourneys" },
  { tag: "Daily Spark", img: `${SHOWCASE}/aurora-daily-spark.webp`, bg: "tile-analytics", k: "tileSpark" },
  { tag: "Calendar", img: `${SHOWCASE}/aurora-calendar.webp`, bg: "tile-analytics", k: "tileCalendar" },
  { tag: "Daily Ritual", img: `${SHOWCASE}/aurora-daily-ritual.webp`, bg: "tile-quests", k: "tileRitual" },
  { tag: "Across Faiths", img: `${SHOWCASE}/aurora-traditions.webp`, bg: "tile-quests", k: "tileTraditions" },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "tracking", label: "Tracking" },
  { key: "insights", label: "Insights" },
  { key: "conflict", label: "Conflict" },
  { key: "connection", label: "Connection" },
  { key: "faith", label: "Faith" },
];

const USE_CASES = [
  { id: 1, cat: "tracking", titleKey: "useCases.items.one.title", bodyKey: "useCases.items.one.body" },
  { id: 2, cat: "insights", titleKey: "useCases.items.two.title", bodyKey: "useCases.items.two.body" },
  { id: 3, cat: "conflict", titleKey: "useCases.items.three.title", bodyKey: "useCases.items.three.body" },
  { id: 4, cat: "insights", titleKey: "useCases.items.four.title", bodyKey: "useCases.items.four.body" },
  { id: 5, cat: "tracking", titleKey: "useCases.items.five.title", bodyKey: "useCases.items.five.body" },
  { id: 6, cat: "connection", titleKey: "useCases.items.six.title", bodyKey: "useCases.items.six.body" },
  { id: 7, cat: "conflict", titleKey: "useCases.items.seven.title", bodyKey: "useCases.items.seven.body" },
  { id: 8, cat: "conflict", titleKey: "useCases.items.eight.title", bodyKey: "useCases.items.eight.body" },
  { id: 9, cat: "conflict", titleKey: "useCases.items.nine.title", bodyKey: "useCases.items.nine.body" },
  { id: 10, cat: "connection", titleKey: "useCases.items.ten.title", bodyKey: "useCases.items.ten.body" },
  { id: 11, cat: "connection", titleKey: "useCases.items.eleven.title", bodyKey: "useCases.items.eleven.body" },
  { id: 12, cat: "connection", titleKey: "useCases.items.twelve.title", bodyKey: "useCases.items.twelve.body" },
  { id: 13, cat: "connection", titleKey: "useCases.items.thirteen.title", bodyKey: "useCases.items.thirteen.body" },
  { id: 14, cat: "insights", titleKey: "useCases.items.fourteen.title", bodyKey: "useCases.items.fourteen.body" },
  { id: 15, cat: "connection", titleKey: "useCases.items.fifteen.title", bodyKey: "useCases.items.fifteen.body" },
  { id: 16, cat: "faith", titleKey: "useCases.items.sixteen.title", bodyKey: "useCases.items.sixteen.body" },
  { id: 17, cat: "faith", titleKey: "useCases.items.seventeen.title", bodyKey: "useCases.items.seventeen.body" },
  { id: 18, cat: "faith", titleKey: "useCases.items.eighteen.title", bodyKey: "useCases.items.eighteen.body" },
  { id: 19, cat: "tracking", titleKey: "useCases.items.nineteen.title", bodyKey: "useCases.items.nineteen.body" },
  { id: 20, cat: "insights", titleKey: "useCases.items.twenty.title", bodyKey: "useCases.items.twenty.body" },
];

const HomePage = () => {
  const { t } = useTranslation(["landing", "site"]);
  const [filter, setFilter] = useState("all");
  const [zoomedTile, setZoomedTile] = useState(null);
  const ts = (k, fb) => t(`site:home.${k}`, fb);

  const TILES = TILE_KEYS.map((tk) => ({ ...tk, caption: ts(tk.k) }));

  const filtered =
    filter === "all"
      ? USE_CASES
      : USE_CASES.filter((u) => u.cat === filter);

  useEffect(() => {
    if (!zoomedTile) return undefined;
    const onEsc = (event) => {
      if (event.key === "Escape") setZoomedTile(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [zoomedTile]);

  return (
    <>
      {/* HERO */}
      <section
        className="relative pb-20 lg:pb-32"
        data-testid={HOME.hero}
      >
        <AuroraBackground variant="hero" />
        <AuroraShaderCanvas className="hero-shader" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-10 lg:pt-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="aurora-chip mb-7">
                <span className="size-1.5 rounded-full bg-[var(--aurora-gold)]" />
                <span>{t("hero.kicker", "Private by design • Faith-aware optional • Built for real relationship seasons")}</span>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="font-serif text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] leading-[1.02] tracking-tight text-white font-light">
                {t("hero.title", "Love is built in small daily moments.").split(" ").slice(0, -3).join(" ")}{" "}
                <span className="italic aurora-shimmer-text">
                  {t("hero.title", "Love is built in small daily moments.").split(" ").slice(-3).join(" ")}
                </span>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 max-w-xl text-white/75 text-lg leading-relaxed font-light">
                {t("hero.subtitle", "Couples rarely drift apart in one big fight. They drift in the missed check-ins, the apology that came out wrong, the same argument on repeat. withAurora.love notices your patterns, coaches you before the hard conversations, and turns everyday moments back into closeness.")}
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8">
                <StoreBadgeGroup startLabel={t("hero.ctaPrimary", "Start Free Now")} />
                <p className="mt-3 text-xs text-white/45 tracking-wide">
                  {ts("freeBadge")}
                </p>
              </div>
            </Reveal>
            <Reveal delay={420}>
              <div className="mt-10 flex flex-wrap gap-2">
                {[
                  t("hero.chip1", "Relationship OS"),
                  t("hero.chip2", "Partner-ready journeys"),
                  t("hero.chip3", "AI Coach + Insights"),
                  t("hero.chip4", "Faith-aware options"),
                  t("hero.chip5", "Private by design"),
                ].map((chip, i) => (
                  <span key={`${chip}-${i}`} className="aurora-chip">
                    {chip}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={200}>
              <div className="relative isolate mx-auto max-w-[340px]">
                {/* Ambient couple photo — warmth behind the product shot.
                    Drop a real licensed image at the path below; gracefully
                    hides itself if the asset is absent. */}
                <img
                  src="/assets/lifestyle/couple-hero.jpg"
                  alt=""
                  aria-hidden="true"
                  loading="eager"
                  fetchpriority="high"
                  className="hero-couple-photo"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div
                  className="absolute -inset-16 rounded-full opacity-40 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(243,165,192,0.45), rgba(78,216,230,0.25) 40%, transparent 70%)",
                    filter: "blur(40px)",
                  }}
                />
                <TiltCard className="relative z-10">
                  <div className="phone-frame phone-frame--device animate-float-slow">
                    <AppVideo
                      className="phone-screen"
                      island
                      sources={[
                        { src: `${CLIP_BASE}/app-preview.mp4`, type: "video/mp4" },
                      ]}
                      poster={`${CLIP_BASE}/app-preview.jpg`}
                      label={t("hero.preview.imgAlt", "A walkthrough of the Aurora app — daily greeting, guided journey and composing a heartfelt note")}
                      soundLabel={t("hero.preview.soundLabel", "Tap for sound")}
                      rounded="49px"
                    />
                  </div>
                </TiltCard>
                {/* Floating nudge card */}
                <div className="absolute z-20 -left-8 -bottom-10 aurora-glass p-4 max-w-[230px] hidden md:block">
                  <p className="eyebrow mb-2">{t("hero.preview.nudgeLabel", "Tonight's gentle nudge")}</p>
                  <p className="text-sm text-white/85 leading-relaxed">
                    {t("hero.preview.nudgeBody", "Plan a slow evening check-in before tomorrow's busy schedule. You'll both feel grounded.")}
                  </p>
                </div>
                <div className="absolute z-20 -right-6 top-10 aurora-glass p-3 hidden md:flex items-center gap-2">
                  <AuroraHeart size={28} />
                  <div>
                    <p className="text-[0.7rem] text-white/55 uppercase tracking-widest">{ts("coachLabel")}</p>
                    <p className="text-sm text-white">{ts("coachReady")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 mt-16 grid sm:grid-cols-3 gap-4">
          {[
            { icon: <Brain size={18} />, ...{ k: "trust.one" }, fallbackT: "Behavior to guidance", fallbackB: "Moods, rituals, sparks, and check-ins become practical next actions." },
            { icon: <Users size={18} />, k: "trust.two", fallbackT: "Built for real seasons", fallbackB: "Use Aurora in a shared partner space or as a private pattern lens across chapters." },
            { icon: <Sparkles size={18} />, k: "trust.three", fallbackT: "Emotion + structure", fallbackB: "Warm UX with enough clarity to reduce repeated loops and friction." },
          ].map((b, i) => (
            <Reveal key={b.k} delay={i * 80}>
              <div className="aurora-glass p-5 h-full">
                <div className="flex items-center gap-3 mb-2">
                  <span className="size-9 rounded-full bg-white/5 flex items-center justify-center text-[var(--aurora-gold)]">
                    {b.icon}
                  </span>
                  <p className="font-serif text-xl">{t(`${b.k}.title`, b.fallbackT)}</p>
                </div>
                <p className="text-white/65 text-[0.95rem] leading-relaxed font-light">
                  {t(`${b.k}.body`, b.fallbackB)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EMPATHY / STORY */}
      <section className="photo-interlude relative py-32" id="story">
        <AuroraBackground variant="soft" />
        {/* Full-bleed lifestyle photo — a couple in a quiet, real moment.
            Drop /assets/lifestyle/couple-story.jpg; hides itself if absent. */}
        <img
          src="/assets/lifestyle/couple-story.jpg"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="photo-interlude__img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="photo-interlude__tint" />
        <div className="grain" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="eyebrow">{t("story.kicker", "Sound familiar?")}</p>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 font-serif text-3xl sm:text-5xl leading-tight tracking-tight font-light text-white">
              {t("story.line1", "It's never really about the dishes.")}
            </p>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-7 text-white/70 text-lg sm:text-xl leading-relaxed font-light">
              {t("story.line2", "It's the check-in that didn't happen. The apology that landed wrong. The same fight, third time this month.")}
            </p>
          </Reveal>
          <Reveal delay={400}>
            <p className="mt-5 text-white/70 text-lg sm:text-xl leading-relaxed font-light">
              {t("story.line3", "You don't need more generic advice. You need to know what to say — tonight, to your person, in your exact situation.")}
            </p>
          </Reveal>
          <Reveal delay={540}>
            <p className="mt-10 font-serif text-2xl sm:text-3xl italic aurora-shimmer-text">
              {t("story.close", "That's what Aurora does.")}
            </p>
          </Reveal>
          <Reveal delay={640}>
            <div className="mt-9 flex justify-center">
              <a
                href="#gallery"
                className="inline-flex items-center gap-2 text-[var(--aurora-cyan)] hover:gap-3 transition-all text-base"
              >
                {t("story.cta", "Show me how")} <ChevronRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* IN THE APP GALLERY */}
      <section className="relative py-24" id="gallery">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow">{ts("galleryEyebrow")}</p>
            <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-2xl">
                {ts("galleryTitle1")} <em className="italic text-[var(--aurora-gold)]/95">{ts("galleryTitle2")}</em> {ts("galleryTitle3")}
              </h2>
              <p className="text-white/65 max-w-md font-light">
                {ts("gallerySubtitle")}
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TILES.map((tile, i) => (
              <Reveal key={tile.tag} delay={i * 70}>
                <div className={`aurora-card ${tile.bg} p-6 flex flex-col items-center text-center h-full overflow-hidden relative`}>
                  <div className="absolute top-4 left-4 aurora-chip !py-1 !px-2.5 !text-[0.65rem]">
                    {tile.tag}
                  </div>
                  <div className="mt-8 mb-5 relative w-full flex justify-center">
                    <div className={`w-full ${tile.imgWrap ?? "max-w-[240px]"} rounded-[20px] border border-white/10 bg-[#12052e]/45 p-1 shadow-2xl overflow-hidden group`}>
                      <button
                        type="button"
                        onClick={() => setZoomedTile(tile)}
                        className="block w-full text-left cursor-zoom-in"
                        aria-label={`Zoom ${tile.tag} image`}
                      >
                        <img
                          src={tile.img}
                          alt={tile.caption}
                          className="w-full h-auto rounded-[16px] block transition-transform duration-300 ease-out group-hover:scale-[1.05]"
                          loading="lazy"
                        />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-white/70 font-light leading-relaxed px-2">
                    {tile.caption}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY ZOOM MODAL */}
      {zoomedTile && (
        <div
          className="fixed inset-0 z-[120] bg-[#070216]/90 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
          onClick={() => setZoomedTile(null)}
        >
          <div className="relative w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close zoom"
              onClick={() => setZoomedTile(null)}
              className="absolute -top-3 -right-3 size-9 rounded-full bg-black/70 border border-white/20 text-white/85 hover:text-white"
            >
              ×
            </button>
            <img
              src={zoomedTile.img}
              alt={zoomedTile.caption}
              className="w-full h-auto rounded-2xl border border-white/15 shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
            />
            <p className="mt-3 text-center text-white/75 text-sm">{zoomedTile.caption}</p>
          </div>
        </div>
      )}

      {/* PILLARS */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow">{t("pillars.kicker", "Built for real connection")}</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-2xl">
              {t("pillars.title", "Designed for real connection.")}
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl text-lg font-light">
              {t("pillars.subtitle", "Four connected pillars that turn everyday moments into deeper intimacy, clarity, and emotional alignment.")}
            </p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { k: "one", icon: <Calendar size={20} />, color: "var(--aurora-journeys)" },
              { k: "two", icon: <Brain size={20} />, color: "var(--aurora-coach)" },
              { k: "three", icon: <MessageCircle size={20} />, color: "var(--aurora-emotional)" },
              { k: "four", icon: <Shield size={20} />, color: "var(--aurora-analytics)" },
            ].map((p, i) => (
              <Reveal key={p.k} delay={i * 70}>
                <div className="aurora-card p-7 h-full relative" style={{ background: p.color }}>
                  <span className="size-11 rounded-2xl bg-white/8 flex items-center justify-center text-[var(--aurora-gold)] mb-5">
                    {p.icon}
                  </span>
                  <h3 className="font-serif text-2xl">
                    {t(`pillars.${p.k}.title`, ["Capture", "Understand", "Connect", "Protect"][i])}
                  </h3>
                  <p className="mt-2 text-white/70 leading-relaxed font-light text-[0.95rem]">
                    {t(`pillars.${p.k}.body`, "")}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={300}>
            <p className="mt-12 text-center font-serif text-2xl italic text-white/75">
              {t("pillars.footerLead", "More clarity. More connection. More us.")}{" "}
              <span className="text-[var(--aurora-gold)] not-italic">{t("pillars.footerAccent", "Every day.")}</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* 20 THINGS / USE CASES */}
      <section id="use-cases" className="relative py-24">
        <AuroraBackground variant="gold" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow">{t("useCases.kicker", "What you can do")}</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-3xl">
              {t("useCases.title", "20 things you can do in withAurora.love right now.")}
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl text-lg font-light">
              {t("useCases.intro", "withAurora.love is not one feature. Track almost any part of life together or privately, including moods, conflicts, travel, books, health, goals, milestones, intimacy, and rituals, then turn those moments into clear guidance.")}
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2" data-testid={HOME.useCaseFilter}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-4 py-2 rounded-full text-sm transition border ${
                  filter === f.key
                    ? "bg-[var(--aurora-gold)] text-[#12052E] border-transparent font-semibold"
                    : "border-white/12 text-white/70 hover:border-white/30"
                }`}
              >
                {t(`useCases.filters.${f.key}`, f.label)}
              </button>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((u, i) => (
              <Reveal key={u.id} delay={(i % 6) * 50}>
                <div className="aurora-card p-6 h-full bg-[var(--aurora-coach)]/65 backdrop-blur-md">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[0.7rem] tracking-[0.2em] uppercase text-[var(--aurora-cyan)]">
                      {u.cat}
                    </span>
                    <span className="text-white/30">·</span>
                    <span className="text-[0.7rem] text-white/40">#{String(u.id).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-serif text-xl leading-snug">
                    {t(u.titleKey, "")}
                  </h3>
                  <p className="mt-2 text-white/65 text-[0.92rem] leading-relaxed font-light">
                    {t(u.bodyKey, "")}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Mid-page CTA */}
          <Reveal delay={100}>
            <div className="mt-14 aurora-glass p-8 sm:p-10 text-center">
              <p className="eyebrow">{t("midCta.kicker", "One small step")}</p>
              <p className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight font-light">
                {t("midCta.title", "Pick one thing from this list. Do it tonight.")}
              </p>
              <p className="mt-4 text-white/70 font-light max-w-lg mx-auto">
                {t("midCta.body", "Free forever plan. No credit card. Two minutes to start — and one better conversation before bed.")}
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <StoreBadgeGroup startLabel={t("midCta.cta", "Start Free Now")} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOCIAL PROOF / TRUST */}
      <Proof />

      {/* PRIVACY */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <p className="eyebrow">{t("security.kicker", "Privacy")}</p>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light">
                {t("security.title", "Security & privacy you control")}
              </h2>
              <p className="mt-4 text-white/70 text-lg font-light max-w-lg">
                {t("security.subtitle", "Private by design—you decide what's shared and when.")}
              </p>
              <ul className="mt-7 space-y-4">
                {["point1", "point2", "point3"].map((p) => (
                  <li key={p} className="flex gap-3 text-white/80">
                    <Lock size={18} className="mt-1 text-[var(--aurora-gold)] shrink-0" />
                    <span className="font-light">{t(`security.${p}`, "")}</span>
                  </li>
                ))}
              </ul>
              <LocaleLink
                to="/privacy-vs-llm"
                className="mt-7 inline-flex items-center gap-2 text-[var(--aurora-cyan)] hover:gap-3 transition-all"
              >
                {ts("privacyLinkText")} <ChevronRight size={16} />
              </LocaleLink>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative">
              <div className="phone-frame phone-frame--device max-w-[360px] lg:ml-auto">
                <AppVideo
                  className="phone-screen"
                  island
                  sources={[
                    { src: `${CLIP_BASE}/data-management-tour.webm`, type: "video/webm" },
                    { src: `${CLIP_BASE}/data-management-tour.mp4`, type: "video/mp4" },
                  ]}
                  poster={`${CLIP_BASE}/data-management-tour.jpg`}
                  label={t("security.previewAlt", "Data Management walkthrough in the Aurora app")}
                  soundLabel={t("security.previewSoundLabel", "Tap for sound")}
                  rounded="49px"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 aurora-glass p-4 max-w-[230px]">
                <p className="eyebrow mb-1">{ts("privacyBadgeTitle")}</p>
                <p className="text-sm text-white/80 font-light leading-relaxed">
                  {ts("privacyBadgeBody")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow">{t("explore.kicker", "Explore")}</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-3xl">
              {t("explore.title", "Go deeper with the pages built for specific questions.")}
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/faq", pill: "FAQ", k: "faq", icon: <CircleHelp size={18} /> },
              { to: "/features", pill: "Product", k: "features", icon: <Sparkles size={18} /> },
              { to: "/best-ai-couples-apps-2026", pill: "Comparison", k: "comparison", icon: <Compass size={18} /> },
              { to: "/faith", pill: "Faith-aware", k: "faith", icon: <Heart size={18} /> },
              { to: "/christian", pill: "Christian", k: "christian", icon: <Heart size={18} /> },
              { to: "/for-men", pill: "For men", k: "men", icon: <Users size={18} /> },
              { to: "/counseling-vs-divorce", pill: "Decision support", k: "counseling", icon: <Brain size={18} /> },
              { to: "/privacy-vs-llm", pill: "Privacy", k: "privacy", icon: <Shield size={18} /> },
              { to: "/roadmap", pill: "Roadmap", k: "roadmap", icon: <Star size={18} /> },
            ].map((c, i) => (
              <Reveal key={c.to} delay={(i % 6) * 50}>
                <LocaleLink
                  to={c.to}
                  className="aurora-card p-6 h-full block group"
                  style={{
                    background: ["var(--aurora-coach)", "var(--aurora-journeys)", "var(--aurora-analytics)", "var(--aurora-emotional)", "var(--aurora-quests)", "var(--aurora-hero)"][i % 6],
                  }}
                  data-testid={`explore-${c.k}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="size-8 rounded-full bg-white/8 flex items-center justify-center text-[var(--aurora-gold)]">
                      {c.icon}
                    </span>
                    <span className="text-[0.7rem] uppercase tracking-[0.2em] text-white/55">
                      {t(`explore.cards.${c.k}.pill`, c.pill)}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl">
                    {t(`explore.cards.${c.k}.title`, "")}
                  </h3>
                  <p className="mt-2 text-white/65 text-[0.92rem] leading-relaxed font-light">
                    {t(`explore.cards.${c.k}.body`, "")}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1 text-[var(--aurora-cyan)] text-sm group-hover:gap-2 transition-all">
                    {t(`explore.cards.${c.k}.cta`, "Open")} <ChevronRight size={14} />
                  </span>
                </LocaleLink>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO INTERLUDE — still choosing each other */}
      <section className="photo-interlude relative py-36 sm:py-44">
        <img
          src="/assets/lifestyle/couple-interlude.jpg"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="photo-interlude__img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="photo-interlude__tint" />
        <div className="grain" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="font-serif text-3xl sm:text-5xl lg:text-6xl italic tracking-tight font-light leading-tight text-white/95">
              {t("interlude.line", "Still here. Still choosing each other.")}
            </p>
            <p className="mt-6 eyebrow !text-white/60">
              {t("interlude.sub", "That's what the small moments are for.")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FUNNEL CTA */}
      <section className="relative py-32">
        <AuroraBackground variant="hero" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="eyebrow">{t("funnel.kicker", "Ready to start?")}</p>
            <h2 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight font-light leading-[1.05]">
              {ts("funnelTitle1")}{" "}
              <em className="italic aurora-shimmer-text">{ts("funnelTitle2")}</em>
            </h2>
            <p className="mt-6 text-white/75 text-lg font-light max-w-xl mx-auto">
              {t("funnel.body", "Two minutes to set up. Free forever, no credit card. And the next hard conversation doesn't have to go the way the last one did.")}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup
                appleTestId={HOME.funnelDownloadApple}
                googleTestId={HOME.funnelDownloadGoogle}
                startTestId={HOME.funnelStartFree}
                startLabel={t("funnel.ctaPrimary", "Start Free Now")}
              />
            </div>
            <p className="mt-4 text-xs text-white/45 tracking-wide">
              {t("site:common.freeFooter")}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default HomePage;
